import { ApolloProvider } from '@apollo/client'
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7'
import { I18nextProvider, useTranslation } from 'react-i18next'
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from 'react-router'
import '@moia-dev/pace-core-web/moia/dark.css'
import '@moia-dev/pace-core-web/moia/light.css'
import { IncludeTheme } from '@moia-dev/pace-web'
import '@moia-dev/pace-web/style.css'
import {
  AuthenticatedAppView,
  getApolloClient,
} from '@backoffice-frontend/authenticated-app-view'
import {
  appConfig,
  configureI18n,
  DEFAULT_LANGUAGE,
  LANGUAGES,
  RefContextProvider,
  Routes,
} from '@backoffice-frontend/common'
import { useAppearance } from '@backoffice-frontend/dark-mode'
import { ENV, Environment } from '@backoffice-frontend/environment'
import { PublicAppView } from '@backoffice-frontend/login-view'
import {
  AdminPanelLogo,
  CssBaseline,
  createTheme,
  darkThemeOptions,
  GlobalStyles,
  moiaThemeOptions,
  StyledEngineProvider,
  SuspenseWrapper,
  ThemeProvider,
} from '@backoffice-frontend/patterns'
import { Layout } from '../Layout'
import { loadSharedAreaTranslations } from '../sharedAreaTranslations'
import { loadSharedLibTranslations } from '../sharedLibTranslations'
import { LoginLayout } from './LoginLayout'
import { loadTranslations } from './locales'
import { useNavigationSections } from './useNavigationSections'

appConfig()

const languageDetectionOptions = [Environment.local, Environment.test].includes(
  ENV,
)
  ? { order: ['localStorage'] }
  : {}

const i18n = configureI18n({
  supportedLngs: LANGUAGES,
  fallbackLng: DEFAULT_LANGUAGE,
  detection: languageDetectionOptions,
})

// Common translations
loadSharedAreaTranslations()
loadSharedLibTranslations()
loadTranslations()

const lightTheme = createTheme(moiaThemeOptions)
const darkTheme = createTheme(darkThemeOptions)
const apolloClient = getApolloClient('admin-panel', [])

export const App = () => {
  const { darkMode } = useAppearance()
  const { t } = useTranslation()

  return (
    <>
      <IncludeTheme theme={darkMode ? 'dark' : 'light'} />
      <I18nextProvider i18n={i18n}>
        <StyledEngineProvider>
          <ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
            <CssBaseline />
            <GlobalStyles colorVariant="grey" />
            <NuqsAdapter>
              <RouterProvider
                router={createBrowserRouter(
                  createRoutesFromElements(
                    <Route element={<Layout />}>
                      <Route
                        path={Routes.Auth.Login.path}
                        element={
                          <LoginLayout>
                            <title>{t('adminPanelApp_tab_title')}</title>
                            <PublicAppView
                              title={t('adminPanelApp_login_title')}
                              description={t('adminPanelApp_login_description')}
                            />
                          </LoginLayout>
                        }
                      />
                      <Route
                        path="/*"
                        element={
                          <ApolloProvider client={apolloClient}>
                            <SuspenseWrapper>
                              <RefContextProvider>
                                <AuthenticatedAppView
                                  useNavigationSections={useNavigationSections}
                                  logo={<AdminPanelLogo />}
                                  logoVariant="dark"
                                />
                              </RefContextProvider>
                            </SuspenseWrapper>
                          </ApolloProvider>
                        }
                      />
                    </Route>,
                  ),
                )}
              />
            </NuqsAdapter>
          </ThemeProvider>
        </StyledEngineProvider>
      </I18nextProvider>
    </>
  )
}
